import * as THREE from 'three';
import { PainterContext, TransitionPainter } from '../types';
import { easeInOutCubic } from '../easing';

/**
 * Sliding Door — two wooden panels slide in from opposite edges and meet
 * in the middle, then slide back off. Ported from the nonogram CSS version
 * into three.js so it stays visually consistent with the other painters.
 */
export function createSlidingDoorPainter(): TransitionPainter {
  let leftDoor: THREE.Mesh | null = null;
  let rightDoor: THREE.Mesh | null = null;
  let ctxRef: PainterContext | null = null;

  return {
    setup(ctx) {
      ctxRef = ctx;
      const woodMaterial = () =>
        new THREE.MeshBasicMaterial({ color: new THREE.Color('#5a2808') });

      const geo = new THREE.PlaneGeometry(1, 4);
      leftDoor = new THREE.Mesh(geo.clone(), woodMaterial());
      rightDoor = new THREE.Mesh(geo.clone(), woodMaterial());
      leftDoor.visible = false;
      rightDoor.visible = false;
      ctx.scene.add(leftDoor);
      ctx.scene.add(rightDoor);
    },
    update(phase, t) {
      if (!leftDoor || !rightDoor || !ctxRef) return;
      const aspect = ctxRef.aspect;
      const halfWidth = aspect; // panel covers half the screen width
      const eased = easeInOutCubic(t);

      leftDoor.scale.x = halfWidth;
      rightDoor.scale.x = halfWidth;

      if (phase === 'exit') {
        // Doors close: slide from off-screen to meeting at center.
        leftDoor.position.x = -aspect - halfWidth / 2 + eased * halfWidth;
        rightDoor.position.x = aspect + halfWidth / 2 - eased * halfWidth;
      } else {
        // Doors open: slide from meeting back off-screen.
        leftDoor.position.x = -halfWidth / 2 - eased * halfWidth;
        rightDoor.position.x = halfWidth / 2 + eased * halfWidth;
      }

      leftDoor.visible = true;
      rightDoor.visible = true;
    },
    hide() {
      if (leftDoor) leftDoor.visible = false;
      if (rightDoor) rightDoor.visible = false;
    },
    dispose() {
      [leftDoor, rightDoor].forEach((m) => {
        if (!m) return;
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      });
      leftDoor = null;
      rightDoor = null;
      ctxRef = null;
    },
  };
}
